//@author: devin

import { NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    input,
    OnChanges,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewEncapsulation,
} from "@angular/core";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25EditableAbstract } from "../s25.editable.abstract";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";

@TypeManagerDecorator("s25-ng-editable-text")
@Component({
    selector: "s25-ng-editable-text",
    template: `
        @if (this.readOnly()) {
            <span class="s25-data">{{ val }}</span>
        } @else {
            @if (!this.editing) {
                <button
                    type="button"
                    class="s25-editable-shared-container c-editable-text editable editable-click"
                    [id]="fieldId()"
                    [attr.aria-labelledby]="ariaLabelledBy()"
                    [attr.aria-label]="ariaLabelText()"
                    (click)="editMode($event)"
                >
                    <ng-container
                        [ngTemplateOutlet]="readonlyTemplate() ?? defaultReadonlyTemplate"
                        [ngTemplateOutletContext]="{ $implicit: val }"
                    ></ng-container>
                </button>
            } @else {
                <input
                    class="firstFocusEditable c-input"
                    type="text"
                    [id]="fieldId()"
                    [attr.aria-labelledby]="ariaLabelledBy()"
                    [attr.aria-label]="ariaLabelText()"
                    [minlength]="min()"
                    [maxlength]="max()"
                    [disabled]="disabled()"
                    [attr.autocomplete]="inputAutocomplete()"
                    [ngModel]="candidateVal"
                    (ngModelChange)="update($event)"
                    (blur)="onInputBlur()"
                    (keyup.enter)="submit()"
                />
                <div class="aw-button-group">
                    @if (hasCancelButton) {
                        <button class="aw-button aw-button--outline" (click)="cancel()" (keyup.enter)="cancel()">
                            {{ cancelButtonText }}
                        </button>
                    }
                    @if (hasCommitButton) {
                        <button
                            class="aw-button aw-button--primary"
                            (click)="commit()"
                            (keyup.enter)="commit()"
                            [disabled]="committing"
                            [class.is-committing]="committing"
                        >
                            {{ committing ? "" : commitButtonText }}
                            <s25-loading-inline [model]="{}"></s25-loading-inline>
                        </button>
                    }
                </div>
                @if (errorMessages) {
                    <div>
                        @for (msg of errorMessages; track msg) {
                            <div class="ngRed ngBold">
                                {{ msg }}
                            </div>
                        }
                    </div>
                }
            }
        }
        <ng-template #defaultReadonlyTemplate let-val>
            <div class="s25-editable s25-data" [style.font-style]="!val && placeholder ? 'italic' : 'none'">
                {{ val || placeholder || "" }}
            </div>
        </ng-template>
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: {
        "[class.ng-editable-text]": "true",
        "[class.ng-editable-text--weak-edit]": "weakEditButton()",
    },
    styles: `
        .ng-editable-text--weak-edit:host {
            .s25-editable-shared-container {
                &:not(:hover, :focus, :active):before {
                    opacity: 0;
                }

                &:before {
                    transition: opacity 0.2s ease-out;
                }
            }
        }

        .s25-data {
            overflow-wrap: break-word;
        }
    `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => S25EditableTextComponent), multi: true }],
    imports: [NgTemplateOutlet, FormsModule, S25LoadingInlineModule],
})
export class S25EditableTextComponent extends S25EditableAbstract<string> implements OnInit, OnChanges {
    public readonly readonlyTemplate = input<TemplateRef<string> | undefined>();
    public readonly weakEditButton = input<boolean>(false);
    public readonly inputAutocomplete = input<string>("off");

    getType = () => "text";

    ngOnChanges(changes: SimpleChanges) {
        if (!changes["val"]?.isFirstChange()) {
            this.valChange.emit(this.val);
        }
    }
}
